import type {
  CameraRateCapabilities, CameraRateReply, CameraRateRemove, CameraRateRequest,
  PlaycastCameraRateMessages, PlaycastMessage, PlaycastMessageTarget,
} from '../types';

const actions = ['getFrameRateCapabilities', 'setFrameRate', 'removeFrameRateDemand'] as const;
const statuses = ['capabilities', 'applied', 'adjusted', 'removed', 'error'];
const errors = ['invalid-request', 'unknown-camera', 'stale-epoch', 'stale-generation',
  'unsupported-rate', 'unsupported-version', 'consumer-limit', 'native-unavailable',
  'deadline-exceeded'];
const cameraPattern = /^ShadowCam0[0-8]$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isId = (value: unknown) => typeof value === 'string' && value.length > 0 && value.length <= 128;
const isInteger = (value: unknown) => Number.isSafeInteger(value) && (value as number) >= 0;
const isInterval = (value: unknown) => Number.isSafeInteger(value)
  && (value as number) >= 27_778 && (value as number) <= 10_000_000;
const isNullable = <T>(value: unknown, predicate: (item: unknown) => item is T): value is T | null =>
  value === null || predicate(value);
const hasExactKeys = (value: Record<string, unknown>, keys: string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);

function isCapabilities(value: unknown): value is CameraRateCapabilities {
  if (!isRecord(value) || !hasExactKeys(value, ['version', 'cameraEpoch', 'minInterval100ns',
    'maxInterval100ns', 'legacyInterval100ns', 'nativeLiveRateChange', 'capacityEvidence',
    'sourceInterval100ns'])) return false;
  return value['version'] === 1 && typeof value['cameraEpoch'] === 'string' && uuidPattern.test(value['cameraEpoch'])
    && isInterval(value['minInterval100ns']) && isInterval(value['maxInterval100ns'])
    && isInterval(value['legacyInterval100ns']) && typeof value['nativeLiveRateChange'] === 'boolean'
    && (value['capacityEvidence'] === 'measured' || value['capacityEvidence'] === 'legacy-baseline')
    && (value['sourceInterval100ns'] === null || isInterval(value['sourceInterval100ns']));
}

function isBase(value: Record<string, unknown>, expectedKeys: string[]): boolean {
  return hasExactKeys(value, expectedKeys) && value['version'] === 1 && isId(value['requestId'])
    && typeof value['cameraName'] === 'string' && cameraPattern.test(value['cameraName']);
}

function isRequest(value: unknown, action: typeof actions[number]): boolean {
  if (!isRecord(value)) return false;
  if (action === 'getFrameRateCapabilities')
    return isBase(value, ['version', 'requestId', 'cameraName']);
  const common = ['version', 'requestId', 'cameraName', 'cameraEpoch', 'consumerId', 'generation'];
  const keys = action === 'setFrameRate'
    ? [...common, 'requestedInterval100ns', 'constraint'] : common;
  if (!isBase(value, keys) || !isId(value['consumerId']) || !isInteger(value['generation'])
    || typeof value['cameraEpoch'] !== 'string' || !uuidPattern.test(value['cameraEpoch'])) return false;
  return action !== 'setFrameRate' || (isInterval((value as CameraRateRequest).requestedInterval100ns)
    && ((value as CameraRateRequest).constraint === 'exact'
      || (value as CameraRateRequest).constraint === 'ideal'));
}

function isReply(value: unknown): value is CameraRateReply {
  if (!isRecord(value) || !hasExactKeys(value, ['version', 'requestId', 'cameraName', 'cameraEpoch',
    'consumerId', 'generation', 'status', 'requestedInterval100ns', 'acceptedInterval100ns',
    'producerInterval100ns', 'appliedGeneration', 'capabilities', 'error'])) return false;
  return value['version'] === 1 && isId(value['requestId']) && typeof value['cameraName'] === 'string'
    && cameraPattern.test(value['cameraName'])
    && isNullable(value['cameraEpoch'], (item): item is string => typeof item === 'string' && uuidPattern.test(item))
    && isNullable(value['consumerId'], (item): item is string => isId(item))
    && isNullable(value['generation'], (item): item is number => isInteger(item))
    && statuses.includes(value['status'] as string)
    && isNullable(value['requestedInterval100ns'], (item): item is number => isInterval(item))
    && isNullable(value['acceptedInterval100ns'], (item): item is number => isInterval(item))
    && isNullable(value['producerInterval100ns'], (item): item is number => isInterval(item))
    && isNullable(value['appliedGeneration'], (item): item is number => isInteger(item))
    && (value['capabilities'] === null || isCapabilities(value['capabilities']))
    && (value['error'] === null || errors.includes(value['error'] as string));
}

export function validateCameraRateMessage(
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastCameraRateMessages> | undefined {
  const { header, body } = message;
  if (header.target !== 'camera' || !actions.includes(header.action as typeof actions[number])) return undefined;
  const valid = header.isReply ? header.source === 'playjector' && isReply(body.message)
    : header.source === 'host' && isRequest(body.message, header.action as typeof actions[number]);
  return valid ? message as PlaycastMessage<PlaycastCameraRateMessages> : undefined;
}
